import { Flex } from '@/components'
import { ReactElement, useState, useEffect, useRef } from 'react'
import { PolarityValue, UpdateMentionRequest } from '@shared/models'
import { Api } from '@/modules'
import styled from 'styled-components'
import { FavorableEmoji, NegativeEmoji, NeutralEmoji, PositiveEmoji, UnfavorableEmoji } from './Polarity.styles'
import { CommandMethods } from '@/modules/api/useCommand'
import loadingImgSrc from '@/assets/images/load.gif'

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`

const DropdownList = styled.ul`
  position: fixed;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  min-width: 180px;
  max-height: 320px;
  overflow-y: auto;
  z-index: 9999;

  /* Seta do dropdown */
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 16px;
    width: 12px;
    height: 12px;
    background-color: #fff;
    border-left: 1px solid #e5e7eb;
    border-top: 1px solid #e5e7eb;
    transform: rotate(45deg);
  }
`

const DropdownItem = styled.li<{ selected?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border: none;
  background: ${({ selected }) => (selected ? '#f0fdf4' : '#fff')};
  font-size: 14px;
  color: #222;
  cursor: pointer;
  transition: background-color 0.2s ease;
  opacity: 1;

  &:hover {
    background-color: #e5e7eb;
  }

  &:active {
    background-color: #e3f2fd;
  }
`

function Emoji({ children }: { children: ReactElement }) {
  return (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {children}
    </svg>
  )
}

function EmojiNeutral({ children }: { children: ReactElement }) {
  return (
    <svg width="25" height="24" viewBox="0 0 88 90" fill="none" xmlns="http://www.w3.org/2000/svg">
      {children}
    </svg>
  )
}

function renderEmoji(polarity: string): JSX.Element {
  switch (polarity) {
    case PolarityValue.NEGATIVE:
      return (
        <NegativeEmoji
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.3999 24C19.0273 24 24.3999 18.6274 24.3999 12C24.3999 5.37261 19.0273 0 12.3999 0C5.77251 0 0.399902 5.37261 0.399902 12C0.399902 18.6274 5.77251 24 12.3999 24ZM5.20327 5.93174C5.18159 5.75801 5.26846 5.5626 5.35532 5.47573C5.38418 5.45845 5.41934 5.44263 5.45772 5.43003C5.50752 5.41362 5.56289 5.40249 5.61753 5.40029C5.6521 5.39897 5.68623 5.40117 5.7186 5.40791C5.74365 5.41318 5.76768 5.42124 5.78965 5.43223L5.90947 5.48042C6.13477 5.57109 6.63677 5.7731 7.25933 6.02197C7.57749 6.14912 7.92715 6.28843 8.2875 6.4314C9.54727 6.93091 10.6333 7.45225 10.7202 7.58247C10.807 7.73452 10.8287 7.90825 10.7635 8.01694C10.7202 8.10381 10.5029 8.16899 10.3075 8.16899C10.0686 8.16899 9.80786 8.45127 9.65581 8.82056C9.48208 9.21152 9.13462 9.5373 8.78701 9.66768C8.75859 9.67471 8.7293 9.68115 8.69941 9.68716C8.67275 9.69243 8.64551 9.69727 8.61782 9.70166C8.54194 9.71367 8.46284 9.72231 8.38345 9.72729C8.12783 9.74356 7.86929 9.72378 7.70112 9.66768C7.4187 9.55898 7.04941 9.2332 6.87568 8.9291C6.61509 8.51646 6.59341 8.23418 6.76714 7.69116C6.82749 7.50996 6.87861 7.33814 6.91597 7.20366C6.95933 7.04795 6.98438 6.94248 6.98438 6.93091C6.98438 6.8874 6.59341 6.71367 6.11558 6.53994C5.63774 6.36621 5.22495 6.08379 5.20327 5.93174ZM14.2605 7.7562C14.2605 7.56079 15.2162 7.03945 16.7583 6.40957C18.1267 5.84487 19.343 5.41055 19.4733 5.43223C19.6036 5.47573 19.7123 5.64946 19.7339 5.84487C19.7774 6.10562 19.495 6.34453 17.9095 6.97441L18.2353 7.62598C18.5177 8.19067 18.5177 8.34272 18.2569 8.82056C18.0832 9.12466 17.6706 9.49395 17.3448 9.64585C16.8669 9.86309 16.6497 9.84141 16.1067 9.58081C15.7157 9.38525 15.4116 9.03779 15.3466 8.71201C15.2741 8.3499 15.0713 8.15449 14.7384 8.12549C14.4778 8.08213 14.2605 7.93008 14.2605 7.7562ZM9.40444 13.8738C9.06987 14.0925 8.74805 14.3347 8.52642 14.5329C8.13545 14.9238 7.57075 15.7274 7.28833 16.314C7.00605 16.9222 6.76714 17.5521 6.76714 17.7258C6.75776 17.8009 6.77681 17.8923 6.81372 17.9842C6.86221 18.1047 6.94146 18.2259 7.02773 18.3122C7.24497 18.5294 7.6793 18.5077 9.04775 18.247C10.0034 18.0732 11.5673 17.9212 12.523 17.9212C13.4786 17.9212 15.0425 18.0732 15.9981 18.247C17.3665 18.5077 17.8009 18.5294 18.018 18.3122C18.1701 18.1601 18.3004 17.8995 18.2788 17.7258C18.2788 17.5521 18.0398 16.9222 17.7574 16.314C17.4751 15.7274 16.9103 14.9238 16.5193 14.5329C16.3948 14.4215 16.2387 14.2963 16.0664 14.1691C15.9231 14.0634 15.7686 13.9562 15.6119 13.8546C15.3327 13.6736 15.0464 13.5103 14.8036 13.4035C14.1737 13.121 13.3701 12.9473 12.523 12.9473C11.6758 12.9473 10.8722 13.121 10.2423 13.4035C9.99097 13.5141 9.69302 13.6853 9.40444 13.8738Z"
          $active
        />
      )
    case PolarityValue.UNFAVORABLE:
      return (
        <UnfavorableEmoji
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.3999 24C19.0273 24 24.3999 18.6274 24.3999 12C24.3999 5.37261 19.0273 0 12.3999 0C5.77251 0 0.399902 5.37261 0.399902 12C0.399902 18.6274 5.77251 24 12.3999 24ZM7.02466 8.16226C6.93779 7.68442 7.06816 7.40215 7.5895 6.88081C8.11069 6.33779 8.37129 6.22925 8.82744 6.33779C9.15322 6.42466 9.5877 6.70708 9.80493 6.96768C10.022 7.22842 10.1959 7.66274 10.1741 7.94517C10.1741 8.20576 9.93516 8.66191 9.63105 8.96602C9.34878 9.26997 8.89263 9.50903 8.61021 9.50903C8.32793 9.53071 7.89346 9.35698 7.63286 9.13975C7.37227 8.92251 7.08984 8.48818 7.02466 8.16226ZM15.33 6.54888C15.3727 6.52192 15.4141 6.49717 15.4531 6.47564C15.5099 6.44399 15.5616 6.41895 15.6041 6.40298C15.7778 6.33779 16.1254 6.33779 16.3644 6.40298C16.6033 6.44648 16.9724 6.70708 17.1896 6.96768C17.4069 7.22842 17.5806 7.70625 17.5589 8.05371C17.5589 8.44468 17.3634 8.79214 16.9724 9.07456C16.625 9.33516 16.1688 9.53071 15.9299 9.53071C15.691 9.50903 15.2783 9.31348 15.0177 9.07456C14.6919 8.79214 14.5181 8.37949 14.5181 7.94517C14.4964 7.53237 14.6701 7.07637 14.8874 6.88081C15.0151 6.76597 15.1802 6.64351 15.33 6.54888ZM11.1516 14.2657C10.804 14.3525 10.1959 14.5915 9.84829 14.7652C9.65947 14.8714 9.36152 15.093 9.04526 15.364C8.77954 15.5917 8.50078 15.854 8.26274 16.1118C7.76323 16.6548 7.35059 17.263 7.35059 17.4368C7.35059 17.6105 7.41563 17.7409 7.50249 17.6974C7.55801 17.6752 7.75518 17.5852 8.03027 17.4558C8.15801 17.3958 8.30244 17.3273 8.45742 17.2534C8.62222 17.1749 8.79873 17.0899 8.97949 17.0023C10.1524 16.4159 10.4999 16.3507 12.3678 16.3507C14.301 16.3507 14.5399 16.3942 15.7778 17.0675C16.5164 17.4368 17.168 17.6974 17.2332 17.6105C17.2983 17.502 17.0376 17.0241 16.6901 16.5245C16.3209 16.0033 15.6693 15.3517 15.2131 15.0476C14.7788 14.7652 13.975 14.4177 13.4322 14.309C13.1395 14.2389 12.8342 14.1813 12.5877 14.1431C12.3769 14.1104 12.209 14.0919 12.1289 14.0919C11.9552 14.1136 11.499 14.1788 11.1516 14.2657Z"
          $active
        />
      )
    case PolarityValue.FAVORABLE:
      return (
        <FavorableEmoji
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.3999 24C19.0273 24 24.3999 18.6274 24.3999 12C24.3999 5.37261 19.0273 0 12.3999 0C5.77251 0 0.399902 5.37261 0.399902 12C0.399902 18.6274 5.77251 24 12.3999 24ZM7.39087 6.62988C7.60811 6.43447 7.93389 6.21724 8.10762 6.15205C8.28135 6.08687 8.54194 6.02168 8.65063 6C8.75918 6 9.12847 6.17373 9.47593 6.43447C9.9104 6.76025 10.0625 7.04253 10.0625 7.62905C10.0625 8.15024 9.9104 8.54121 9.58462 8.82363C9.3022 9.06255 8.84605 9.25796 8.54209 9.25796C8.23799 9.25796 7.78184 9.04087 7.49941 8.75845C7.21714 8.47603 6.9999 8.02002 6.9999 7.62905C6.9999 7.25977 7.17363 6.82529 7.39087 6.62988ZM14.667 6.84712C14.8191 6.6082 15.1232 6.32578 15.3838 6.21724C15.6227 6.10855 15.9485 6.02168 16.1005 6.02168C16.2309 6 16.6652 6.28242 17.0128 6.65156C17.5124 7.15122 17.6427 7.4335 17.5559 7.91133C17.4907 8.23711 17.2083 8.67158 16.9477 8.88882C16.6871 9.10591 16.2526 9.27979 15.992 9.25796C15.7096 9.25796 15.2536 9.01904 14.9495 8.71494C14.6454 8.41099 14.4064 7.97651 14.4064 7.7376C14.4064 7.62612 14.43 7.48154 14.4708 7.33272C14.5172 7.16235 14.5859 6.98613 14.667 6.84712ZM7.43438 15.0354C7.45606 15.1875 7.73833 15.7088 8.06426 16.165C8.41172 16.5993 9.21533 17.2292 9.86689 17.5768C10.7357 18.0111 11.3439 18.1632 12.2344 18.1632C12.9511 18.1632 13.8199 17.9895 14.4064 17.707C14.6127 17.6162 14.844 17.4942 15.0694 17.3599C15.4375 17.1406 15.79 16.8883 15.992 16.6862C16.3395 16.3604 16.7522 15.8609 16.926 15.5568C17.078 15.2527 17.1431 14.9486 17.0562 14.8617C16.9693 14.7749 16.6002 14.8835 16.2526 15.1006C15.9511 15.3079 15.2407 15.5971 14.5897 15.7976C14.4904 15.8282 14.3925 15.8568 14.2978 15.8826C13.3421 16.165 12.6037 16.2302 11.5828 16.1215C10.714 16.0346 9.62798 15.7305 8.80269 15.3614C7.76016 14.8617 7.43438 14.7965 7.43438 15.0354Z"
          $active
        />
      )
    case PolarityValue.NO_RULE:
      return (
        <svg width="25" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="20" fill="white" stroke="#333" strokeWidth="4" />
          <path d="M24 14C20.686 14 18 16.239 18 19" stroke="#333" strokeWidth="4" strokeLinecap="round" />
          <path
            d="M24 14C27.314 14 30 16.239 30 19C30 22 27 23 25 24.5C23.5 25.5 23.5 27 23.5 28"
            stroke="#333"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <circle cx="24" cy="34" r="2.5" fill="#333" />
        </svg>
      )
    case PolarityValue.NEUTRAL:
      return (
        <NeutralEmoji
          fillRule="evenodd"
          clipRule="evenodd"
          d="M44 0C19.6995 0 0 19.9731 0 44.6111C0 69.2492 19.6995 89.2222 44 89.2222C68.3005 89.2222 88 69.2492 88 44.6111C88 19.9731 68.3005 0 44 0ZM30 28.3887C32.2091 28.3887 34 30.2044 34 32.4442V34.472C34 36.7118 32.2091 38.5276 30 38.5276C27.7909 38.5276 26 36.7118 26 34.472V32.4442C26 30.2044 27.7909 28.3887 30 28.3887ZM58 28.3887C60.2091 28.3887 62 30.2044 62 32.4442V34.472C62 36.7118 60.2091 38.5276 58 38.5276C55.7909 38.5276 54 36.7118 54 34.472V32.4442C54 30.2044 55.7909 28.3887 58 28.3887ZM30 54.7498C27.7909 54.7498 26 56.5655 26 58.8053C26 61.0452 27.7909 62.8609 30 62.8609H58C60.2091 62.8609 62 61.0452 62 58.8053C62 56.5655 60.2091 54.7498 58 54.7498H30Z"
          $active
        />
      )
    default:
      return (
        <PositiveEmoji
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.3999 24C19.0273 24 24.3999 18.6274 24.3999 12C24.3999 5.37261 19.0273 0 12.3999 0C5.77251 0 0.399902 5.37261 0.399902 12C0.399902 18.6274 5.77251 24 12.3999 24ZM7.02979 6.95977C7.09497 6.61231 7.42075 6.22134 7.76836 6.02578C7.94224 5.93306 8.11611 5.85264 8.25689 5.79448C8.31035 5.77251 8.35913 5.75361 8.40132 5.73853C8.4561 5.7189 8.49976 5.70571 8.52847 5.7C8.61533 5.7 8.91943 5.83037 9.22354 5.98242C9.52764 6.11265 9.8751 6.48193 10.0055 6.78604C10.1792 7.19868 10.1792 7.4811 9.94028 7.98062C9.78823 8.32808 9.39727 8.71904 9.07148 8.84941C8.65884 9.00146 8.33306 9.00146 7.94209 8.80591C7.63799 8.65386 7.2687 8.3064 7.13848 8.0458C7.102 7.96685 7.07241 7.87251 7.05 7.77056C7.03008 7.68003 7.01602 7.5835 7.00796 7.48623C7.00137 7.40684 6.99873 7.327 7.00034 7.24951C7.00254 7.14639 7.01221 7.04751 7.02979 6.95977ZM14.4363 7.61133C14.3495 7.1335 14.4798 6.85122 15.001 6.32988C15.5224 5.78687 15.783 5.67832 16.2391 5.78687C16.5649 5.87373 16.9994 6.15615 17.2165 6.41675C17.4337 6.67734 17.6074 7.11182 17.5857 7.39409C17.5857 7.65483 17.3468 8.11099 17.0427 8.41494C16.7605 8.71904 16.3043 8.95796 16.0219 8.95796C15.7396 8.97979 15.3051 8.80591 15.0445 8.58882C14.7839 8.37158 14.5015 7.93711 14.4363 7.61133ZM7.2687 13.5192C7.20352 13.6496 7.29053 14.1491 7.42075 14.6269C7.5728 15.1264 8.05063 15.9302 8.46328 16.408C8.87607 16.8858 9.6145 17.4722 10.0923 17.7111C10.6715 18.0007 11.3956 18.1456 12.2644 18.1456C13.1332 18.1456 13.8571 18.0007 14.4363 17.7111C14.9142 17.4722 15.6527 16.8858 16.0654 16.408C16.478 15.9302 16.9559 15.1047 17.1079 14.5835C17.1669 14.381 17.2129 14.1784 17.2408 14.0013C17.2844 13.7227 17.283 13.5072 17.2165 13.454C17.1296 13.3455 15.9567 13.4323 14.6536 13.6496C12.3946 14.0187 12.1557 14.0187 10.0923 13.6712C8.89775 13.454 7.81172 13.302 7.65967 13.302C7.50762 13.302 7.35557 13.3888 7.2687 13.5192Z"
          $active
        />
      )
  }
}

export function PolarityEmoji({ value }: { value: string }) {
  return (
    <Flex $type="row" $justify="center" $align="center" $gap={4}>
      {value === PolarityValue.NEUTRAL ?
        <EmojiNeutral>{renderEmoji(value)}</EmojiNeutral>
      : <Emoji>{renderEmoji(value)}</Emoji>}
    </Flex>
  )
}

export function PolarityDropdown({
  polarity,
  brandID,
  mentionID,
  companyID,
  onUpdated,
}: {
  polarity: string
  mentionID: string
  companyID: string
  brandID: string
  onUpdated?: (newPolarity: string) => void
}) {
  const [open, setOpen] = useState(false)
  const selected = polarity
  const { isRunning: isUpdatingMention, run: updateMention } = Api.Command<UpdateMentionRequest>(
    CommandMethods.PATCH,
    `companies/:companyID/mentions/:mentionID`,
  )
  const triggerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLUListElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const [hovered, setHovered] = useState(false)

  const polarities = [
    PolarityValue.POSITIVE,
    PolarityValue.NEGATIVE,
    PolarityValue.FAVORABLE,
    PolarityValue.UNFAVORABLE,
  ]

  useEffect(() => {
    if (!open) return
    function handleClickOutside(event: MouseEvent) {
      if (!triggerRef.current?.contains(event.target as Node) && !dropdownRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open && triggerRef.current && dropdownRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownStyle({
        left: rect.left,
        top: rect.bottom,
        width: rect.width,
      })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function updateDropdownPosition() {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setDropdownStyle({
          left: rect.left,
          top: rect.bottom,
          width: rect.width,
        })
      }
    }
    window.addEventListener('scroll', updateDropdownPosition, true)
    window.addEventListener('resize', updateDropdownPosition)
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true)
      window.removeEventListener('resize', updateDropdownPosition)
    }
  }, [open])

  const handleSelect = (newPolarity: string) => {
    if (newPolarity === selected) {
      setOpen(false)
      return
    }
    updateMention({
      input: { polarityInternalID: getPolarityInternalID(newPolarity), brandID: brandID },
      binds: [
        ['companyID', companyID],
        ['mentionID', mentionID],
      ],
      onSuccess: () => {
        setOpen(false)
        onUpdated?.(newPolarity)
      },
      onFail: () => {
        setOpen(false)
      },
    })
  }

  function getPolarityInternalID(polarity: string): number | undefined {
    switch (polarity) {
      case PolarityValue.POSITIVE:
        return 3
      case PolarityValue.NEGATIVE:
        return 4
      case PolarityValue.FAVORABLE:
        return 1
      case PolarityValue.UNFAVORABLE:
        return 2
      case PolarityValue.NEUTRAL:
        return 5
      case PolarityValue.NO_RULE:
        return 6
      default:
        return undefined
    }
  }

  return (
    <DropdownContainer>
      <div
        ref={triggerRef}
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minHeight: 32 }}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        {isUpdatingMention ?
          <img alt="loading" src={loadingImgSrc} style={{ width: 24, height: 24, marginRight: 4 }} />
        : <>
            <PolarityEmoji value={selected} />
            {hovered && <span style={{ fontSize: 12, marginLeft: 4 }}>▼</span>}
          </>
        }
      </div>
      {open && (
        <DropdownList
          ref={dropdownRef}
          style={{
            ...dropdownStyle,
            position: 'fixed',
          }}>
          {polarities.map((p) => (
            <DropdownItem key={p} selected={p === selected} onClick={() => handleSelect(p)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <PolarityEmoji value={p} />
                <span>{p}</span>
              </div>
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </DropdownContainer>
  )
}
